import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { parseZone } from 'moment';

import { Photographer } from '../../../models/common/photographer.models';
import { AutocompleteSettings } from '../../../shared/models/autocomplete-settings.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Warehouse } from '../../../models/common/warehouse.model';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { CompanyStyle } from '../../../models/company/company-style.model';
import { UtilitiesService } from '../../../shared/services/utilities.service';
import { AppService } from '../../../app.service';
import { ModelColorSize } from '../../../models/photographer/create-order.model';
import { Color } from '../../../models/common/color.model';
import { Ids } from '../../../models/common/ids.model';
import { GraphQLService } from '../../../services/graph-ql.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { map } from 'rxjs';
import { WrapperResultGraphQL } from '../../../models/shared/types/wrapper-result-graphql.type';
import { PageInfo } from '../../../models/shared/types/page-info.type';
import { MutationQuery } from '../../../models/shared/enums/mutation-query.enum';

@Component({
  standalone: false,
  selector: 'bt-sidebar-customorder',
  templateUrl: './sidebar-customorder.component.html',
  styleUrls: ['./sidebar-customorder.component.scss']
})
export class SidebarCustomorderComponent implements OnInit {

  @Input() photographer?: Photographer;
  @Output() created: EventEmitter<void> = new EventEmitter<void>();
  @Output() error: EventEmitter<void> = new EventEmitter<void>();

  readonly #destroyRef = inject(DestroyRef);
  readonly #utilitiesService = inject(UtilitiesService);
  readonly #appService = inject(AppService);
  readonly #graphQLService = inject(GraphQLService);

  photographers: { data: Array<Photographer>, page?: number, selected?: Photographer, search?: string, settings: AutocompleteSettings } = {
    data: [],
    settings: new AutocompleteSettings({
      valueProperty: 'personId',
      textProperty: 'fullName',
      paginationSize: 50,
      serverPagination: true
    })
  };

  warehouses: { data: Array<Warehouse>, page?: number, search?: string, selected?: Warehouse, autocompleteSettings: AutocompleteSettings, loaderId: string, pageInfo: PageInfo } = {
    data: [],
    autocompleteSettings: new AutocompleteSettings({
      textProperty: 'fullName',
      valueProperty: 'id',
      serverPagination: true,
      paginationSize: 50
    }),
    loaderId: UtilityFunctions.newGuid(),
    pageInfo: {
      endCursor: null,
      hasNextPage: false,
      hasPreviusPage: false,
      startCursor: null
    }
  };

  styles: { data: Array<CompanyStyle>, selected?: CompanyStyle, autocompleteSettings: AutocompleteSettings, loaderId: string, search?: string, page?: number } = {
    data: [],
    autocompleteSettings: new AutocompleteSettings({
      textProperty: 'fullName',
      valueProperty: 'modelId',
      serverPagination: true,
      paginationSize: 50
    }),
    loaderId: UtilityFunctions.newGuid()
  };

  colors: { data: Array<Color>, nextData?: Array<Color>, selections?: Array<Color>, multiselectSettings: AutocompleteSettings, page?: number, search: string, loaderId: string, pageInfo: PageInfo } = {
    data: [],
    multiselectSettings: new AutocompleteSettings({
      textProperty: 'fullName',
      valueProperty: 'colorId',
      paginationSize: 50,
      serverPagination: true
    }),
    loaderId: UtilityFunctions.newGuid(),
    search: '',
    pageInfo: {
      endCursor: null,
      hasNextPage: false,
      hasPreviusPage: false,
      startCursor: null
    }
  };

  chips: { style: CompanyStyle, colors: Array<Color>, title: string }[] = [];
  dueDate: string;
  comments: string;
  loaderId = UtilityFunctions.newGuid();
  todayDate: string;

  constructor() {
    this.todayDate = parseZone(new Date()).format('yyyy-MM-DD');
  }

  ngOnInit(): void {
    if (!this.photographer) {
      this.getPhotographers(0);
    }
  }

  createOrder() {
    if (this.photographers.selected && this.warehouses.selected && this.dueDate && this.chips.length) {

      const data: Array<ModelColorSize> = [];
      this.chips.forEach(s => {
        s.colors.forEach(c => data.push({ colorId: c.colorId, modelId: s.style.modelId, sizeGroupId: undefined }));
      });

      const payload = {
        photographerId: !!this.photographer ? this.photographer.personId! : this.photographers.selected.personId!,
        warehouseId: this.warehouses.selected.id,
        stylesColorsSizes: data,
        endDate: parseZone(this.dueDate).utc(true).toISOString(),
        companyId: this.#appService.companyId,
        comments: this.comments,
        userId: this.#appService.user.userId
      }

      this.#graphQLService.postData(MutationQuery.CREATE_ORDER, payload)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (value: any) => {
            this.#utilitiesService.showSnackbar(value.message);
            this.created.emit();
          },
          error: (err) => {
            console.error(err);
            this.#utilitiesService.showSnackbar(err);
            this.error.emit();
          }
        })
    } else {
      this.#utilitiesService.showSnackbar('Missing fields');
    }
  }

  onSelectPhotographer(p: Photographer) {
    this.photographers.selected = p;
  }

  getPhotographers(page: number) {
    this.photographers.page = page;

    const filters = {
      companyId: this.#appService.companyId,
      paginationPage: this.photographers.page,
      paginationSize: this.photographers.settings.paginationSize,
      search: '',
      statusIds: [],
      photographerIds: []
    };

    this.#graphQLService.getData<Photographer>(GetQuery.GET_PHOTOGRAPHERS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['photographers']))
      .subscribe((data: WrapperResultGraphQL<Photographer>) => {
        const newData = data.nodes.map(el => ({ ...el, displayId: el.personId!, displayName: el.fullName!, selected: true, disabled: false }));
        this.photographers.data = !this.photographers.page ? newData : this.photographers.data.concat(newData);
      });
  }

  onPhotographersFilterChange(search: string) {
    this.photographers.search = search ? search.toLowerCase() : '';
    this.getPhotographers(0);
  }

  onOpenWarehousesAutocomplete() {
    if (!this.warehouses.data.length) this.getWarehouses(true);
  }

  onSelectWarehouse(wh: Warehouse) {
    this.warehouses.selected = wh;
    this.resetStyles();
    this.resetColors();
  }

  onOpenStylesAutocomplete() {
    if (!this.styles.data?.length) this.getStyles(0);
  }

  onSelectStyle(s: CompanyStyle) {
    this.styles.selected = s;
    this.resetColors();
  }

  onStylesFilterChange(text: string) {
    this.styles.search = text.toLowerCase() || '';
    this.getStyles(0);
  }

  getStyles(page: number) {
    this.styles.page = page;

    const filters = {
      companyId: this.#appService.companyId,
      paginationPage: this.styles.page ?? 0,
      paginationSize: this.styles.autocompleteSettings.paginationSize,
      search: this.styles.search ?? '',
      notAssigned: false,
      brandIds: [],
      categoryIds: [],
      photographersIds: [],
      warehousesIds: [new Ids(this.warehouses.selected!.id)],
      styleGroupIds: [],
      styleDescriptionIds: [],
      purposeIds: [],
      teamIds: [],
      suggestedSeasonIds: []
    };

    this.#graphQLService.getData<CompanyStyle>(GetQuery.GET_COMPANY_STYLES, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['companyStyles']))
      .subscribe({
        next: (data: WrapperResultGraphQL<any>) => {
          this.styles.data = !this.styles.page ? data.nodes[0].datasource.map(el => ({ ...el })) : this.styles.data.concat(data.nodes[0].datasource.map(el => ({ ...el })));
        }
      });
  }

  onOpenColorsMultiselect() {
    if (!this.colors.data.length) this.getColors(true);
  }

  onSelectColors(colors: Color[]) {
    this.colors.selections = colors;
  }

  onColorsFilterChange(text: string) {
    this.colors.search = text.toLowerCase() || '';
    this.getColors();
  }

  getColors(firstOpen?: boolean) {
    if (firstOpen) this.colors.pageInfo.endCursor = null;
    const filter = {
      first: this.colors.multiselectSettings.paginationSize,
      after: this.colors.pageInfo.endCursor,
      modelId: this.styles.selected!.modelId,
      search: this.colors.search ?? ''
    };

    this.#graphQLService.getData<Color>(GetQuery.GET_COLORS_BY_MODEL, filter)
      .pipe(map(res => res['data']['colors']), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: WrapperResultGraphQL<any>) => {
          this.colors.data = !this.colors.pageInfo.endCursor ? data.nodes.map(el => ({ colorId: el.id, colorCode: el.code, colorName: el.finalColorName, fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}` })) : this.colors.data.concat(data.nodes.map(el => ({ ...el })));
          this.colors.pageInfo = { ...data.pageInfo };
        }
      });
  }

  onAddStyle() {
    if (this.chips.some(x => x.style.modelId === this.styles.selected.modelId)) {
      this.#utilitiesService.showSnackbar('Conflicting selection');
    } else {
      const chip = {
        style: this.styles.selected,
        colors: this.colors.selections,
        title: 'Colors: ',
      };
      chip.colors.forEach((c, i) => chip.title = chip.title + (!i ? c.colorCode : (' - ' + c.colorCode)));
      this.chips.push(chip);
      this.resetStyles();
      this.resetColors();
    }
  }

  removeStyle(c: { style: CompanyStyle, colors: Color[], title: string }) {
    this.chips = this.chips.filter(x => x.style.modelId !== c.style.modelId);
  }

  getWarehouses(firstOpen?: boolean) {
    if (firstOpen) this.warehouses.pageInfo.endCursor = null;
    this.#graphQLService
      .getData<any>(GetQuery.GET_WAREHOUSES_NO_IDS, { first: this.warehouses.autocompleteSettings.paginationSize, after: this.warehouses.pageInfo.endCursor, search: this.warehouses.search ?? '' })
      .pipe(map(res => res['data']['warehouses']), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (data: WrapperResultGraphQL<Warehouse>) => {
          const newData = data.nodes.map(el => {
            const element: Warehouse = {
              ...el,
              fullName: `${el.code.trim()} - ${el.description.trim()}`
            }
            return element;
          });
          this.warehouses.data = this.warehouses.pageInfo.endCursor ? this.warehouses.data.concat(newData) : newData;

          this.warehouses.pageInfo = { ...data.pageInfo };
        }
      });
  }

  onWarehousesFilterChange(search: string) {
    this.warehouses.search = search;
    this.getWarehouses(true);
  }

  private resetStyles() {
    this.styles.data = [];
    this.styles.selected = undefined;
  }

  private resetColors() {
    this.colors.data = [];
    this.colors.nextData = undefined;
    this.colors.selections = undefined;
  }
}
